import { useEffect, useState } from 'react'
import { CloseIcon } from '@/components/icons/baba-icons'
import { trackAppStoreClick } from '@/lib/analytics'
import { getChromeStoreUrl } from '@/lib/storeLinks'

const DISMISS_KEY = 'baba.app.extension_promo_dismissed_at'
const DISMISS_FOR_MS = 7 * 24 * 60 * 60 * 1000

function wasRecentlyDismissed(): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY)
    if (!raw) return false
    const dismissedAt = Number(raw)
    return Number.isFinite(dismissedAt) && Date.now() - dismissedAt < DISMISS_FOR_MS
  } catch {
    return false
  }
}

/** True desktop Chrome only — the extension can't install anywhere else. */
function isDesktopChrome(): boolean {
  if (typeof navigator === 'undefined') return false
  const uaData = (navigator as Navigator & { userAgentData?: { mobile?: boolean; brands?: { brand: string }[] } })
    .userAgentData
  if (uaData) {
    if (uaData.mobile) return false
    return uaData.brands?.some((b) => b.brand === 'Google Chrome') ?? false
  }
  const ua = navigator.userAgent || ''
  if (/Mobi|Android|iPhone|iPad|iPod/i.test(ua)) return false
  return /Chrome\//i.test(ua) && !/Edg\/|OPR\/|Brave\//i.test(ua)
}

/**
 * Dismissible desktop-Chrome strip pointing at the extension — the fastest
 * install path for anyone already living in the web app. Never shown on
 * mobile (there's no extension to install there) and never more than once
 * per 7 days after a dismissal. No "already installed" detection exists yet
 * in this app, so it always shows for eligible visitors.
 */
export default function ExtensionPromo() {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (!isDesktopChrome()) return
    if (wasRecentlyDismissed()) return
    setVisible(true)
  }, [])

  if (!visible) return null

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()))
    } catch {
      // Best-effort — worst case the strip reappears next visit.
    }
    setVisible(false)
  }

  return (
    <div className="fixed bottom-4 right-4 z-40 flex items-center gap-3 rounded-md border border-app bg-surface px-3.5 py-2.5 shadow-token-md max-w-[380px]">
      <img
        src="/baba-app-icon.png"
        alt=""
        className="h-6 w-6 shrink-0 object-cover"
        style={{ borderRadius: '4px' }}
      />
      <p className="flex-1 text-[13px] text-app leading-snug">
        <span className="font-semibold">baba for Chrome</span>
        <span className="text-muted">: Highlight Any Hebrew to Translate It</span>
      </p>
      <a
        href={getChromeStoreUrl('web_app_promo')}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackAppStoreClick('chrome', 'web_app_promo')}
        className="shrink-0 h-[30px] px-3.5 rounded-md text-[12.5px] font-semibold flex items-center transition-all duration-150 hover:brightness-105 active:scale-95 shadow-sm shadow-brand-500/20 cursor-pointer select-none"
        style={{ background: 'var(--accent)', border: '1px solid var(--accent-deep)', color: 'var(--cta-fg)' }}
      >
        Add to Chrome
      </a>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={dismiss}
        className="shrink-0 h-7 w-7 flex items-center justify-center rounded-md text-subtle hover:text-app hover:bg-[var(--hover)] transition-colors duration-150 cursor-pointer"
      >
        <CloseIcon size={10} />
      </button>
    </div>
  )
}
